import PropTypes from "prop-types"
import React, { useEffect } from "react"
import "bootstrap/dist/css/bootstrap.min.css"
import "bootstrap/dist/js/bootstrap.bundle.min.js"
import "./assets/css/calendarStyles.css"
import "react-big-calendar/lib/css/react-big-calendar.css"
import "react-grid-layout/css/styles.css"
import "react-resizable/css/styles.css"

import { Routes, Route } from "react-router-dom"
// Import Routes all
import { authProtectedRoutes, publicRoutes } from "./routes"

// Import all middleware
import Authmiddleware from "./routes/route"

import HorizontalLayout from "./components/HorizontalLayout/"
import NonAuthLayout from "./components/NonAuthLayout"

// Import scss
import "./assets/scss/theme.scss"
import "./App.css"
import AccessDenied from "routes/AccessDenied"
import { socket } from "utils/Connection"
import { AppContext } from "AppContext"

const App = () => {
  const Layout = HorizontalLayout

  useEffect(() => {
    const requestNotificationPermission = async () => {
      try {
        // Check if the browser supports notifications
        if (!("Notification" in window)) {
          console.log("This browser does not support desktop notifications")
          return
        }

        // Request permission if not already granted
        if (Notification.permission !== "granted") {
          const permission = await Notification.requestPermission()
          if (permission !== "granted") {
            console.log("Notification Permission Denied by User")
            return
          }
        }
      } catch (error) {
        console.error("Error requesting notification permission:", error)
      }
    }

    const handleTimesheetSubmissionNotification = data => {
      try {
        if (Notification.permission === "granted") {
          new Notification(`📅 Timesheet Received`, {
            body: `${data.employee.name} has submitted their timesheet with InTime - ${data.timing.inTime} & OutTime - ${data.timing.outTime} for location - ${data.location.name}`,
            icon: "/logo.jpg",
            tag: `timesheet-${data.timesheetId}`, // Prevents duplicate notifications
            requireInteraction: false, // Notification remains until user interacts
            silent: false,
            vibrate: [200, 100, 200], // Vibration pattern for mobile
          })
        }
      } catch (error) {
        console.error("Error showing notification:", error)
      }
    }

    const handleLeaveRequestNotification = data => {
      try {
        if (Notification.permission === "granted") {
          new Notification(`🌴 Leave Request Received`, {
            body: `${data.employee.name} has submitted a leave request from - ${data.fromDate} & OutTime - ${data.toDate}`,
            icon: "/logo.jpg",
            tag: `leave-${data.requestId}`, // Prevents duplicate notifications
            requireInteraction: false, // Notification remains until user interacts
            silent: false,
            vibrate: [200, 100, 200], // Vibration pattern for mobile
          })
        }
      } catch (error) {
        console.error("Error showing notification:", error)
      }
    }

    const handleUnavailabilityRequestNotification = data => {
      try {
        if (Notification.permission === "granted") {
          new Notification(`❌ Unavailability Received`, {
            body: `${data.employee.name} has submitted their unavailability for InTime - ${data.timing.inTime} & OutTime - ${data.timing.outTime} for location - ${data.location.name}`,
            icon: "/logo.jpg",
            tag: `unavailability-${data.requestId}`, // Prevents duplicate notifications
            requireInteraction: false, // Notification remains until user interacts
            silent: false,
            vibrate: [200, 100, 200], // Vibration pattern for mobile
          })
        }
      } catch (error) {
        console.error("Error showing notification:", error)
      }
    }

    const handleQueryNotification = data => {
      try {
        if (Notification.permission === "granted") {
          new Notification(`📩 New Query Received`, {
            body: `${data.employee.name} has submitted a new query: ${data.title}`,
            icon: "/logo.jpg",
            tag: `query-${data.queryId}`,
            requireInteraction: false,
            silent: false,
            vibrate: [200, 100, 200],
          })
        }
      } catch (error) {
        console.error("Error showing notification:", error)
      }
    }

    requestNotificationPermission()

    // Listen for events
    socket.on("timesheetSubmitted", handleTimesheetSubmissionNotification)
    socket.on("leaveRequest", handleLeaveRequestNotification)
    socket.on("unavailabilityReceived", handleUnavailabilityRequestNotification)
    socket.on("queryReceived", handleQueryNotification)

    // --- Global data refresh trigger ---
    // Any page listening to "data-changed" re-fetches its own data,
    // so new data from the mobile app / backend / other admin sessions
    // shows up without a manual page refresh.
    const notifyDataChanged = () => {
      console.log("[Realtime] data-changed dispatched")
      window.dispatchEvent(new Event("data-changed"))
    }

    const notifyDataChangedWithDelay = () => {
      // Fire immediately and again a few seconds later, because the socket
      // event can arrive before the backend has finished saving the record.
      notifyDataChanged()
      setTimeout(notifyDataChanged, 3000)
    }

    socket.on("timesheetSubmitted", notifyDataChangedWithDelay)
    socket.on("leaveRequest", notifyDataChangedWithDelay)
    socket.on("unavailabilityReceived", notifyDataChangedWithDelay)
    socket.on("queryReceived", notifyDataChangedWithDelay)

    // Safety net for changes that don't emit socket events
    const pollTimer = setInterval(notifyDataChanged, 60000)

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") notifyDataChanged()
    }
    document.addEventListener("visibilitychange", onVisibilityChange)

    // Cleanup
    return () => {
      socket.off("timesheetSubmitted", handleTimesheetSubmissionNotification)
      socket.off("leaveRequest", handleLeaveRequestNotification)
      socket.off("unavailabilityReceived", handleUnavailabilityRequestNotification)
      socket.off("queryReceived", handleQueryNotification)
      socket.off("timesheetSubmitted", notifyDataChangedWithDelay)
      socket.off("leaveRequest", notifyDataChangedWithDelay)
      socket.off("unavailabilityReceived", notifyDataChangedWithDelay)
      socket.off("queryReceived", notifyDataChangedWithDelay)
      clearInterval(pollTimer)
      document.removeEventListener("visibilitychange", onVisibilityChange)
    }
  }, [])

  return (
    <React.Fragment>
      <AppContext>
        <Routes>
          <Route path="*" element={<AccessDenied />} />
          {publicRoutes.map((route, idx) => (
            <Route
              path={route.path}
              element={<NonAuthLayout>{route.component}</NonAuthLayout>}
              key={idx}
              exact={true}
            />
          ))}

          {authProtectedRoutes.map((route, idx) => (
            <Route
              path={route.path}
              element={
                <Authmiddleware>
                  <Layout>{route.component}</Layout>
                </Authmiddleware>
              }
              key={idx}
              exact={true}
            />
          ))}
        </Routes>
      </AppContext>
    </React.Fragment>
  )
}

App.propTypes = {
  layout: PropTypes.any,
}

export default App
